import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';

import behavioralCaseManagerIcon from '../assets/bahavioural_manager.svg';
import compassCurriculumIcon from '../assets/compass.svg';
import digitalThreatDetectionIcon from '../assets/Digital_Thread.svg';
import emergencyManagementIcon from '../assets/emergency_management.svg';
import hallPassIcon from '../assets/hall_pass.svg';
import pbisRewardsIcon from '../assets/pbis_reward.svg';
import visitorManagementIcon from '../assets/visitor_manager.svg';

import {
	makeStyles,
	Popover,
	Divider,
	Typography,
	Tooltip
} from '@material-ui/core';

const BORDER_COLOR = '#D9DCDD';
const CURRENT_APP_TOOLTIP = 'This application is already open.';
const NUNITO_SANS = '"Nunito Sans", system-ui, -apple-system, sans-serif';

const PRODUCT_CATEGORIES = [
	{
		name: 'Safety and Emergency Response',
		products: [
			{ name: 'Digital Threat Detection', url: null, icon: digitalThreatDetectionIcon },
			{ name: 'Emergency Management', url: 'https://hubs.ly/Q04tm1TH0', icon: emergencyManagementIcon },
			{ name: 'Hall Pass', url: 'https://hubs.la/Q04v3dd50', icon: hallPassIcon },
			{ name: 'Visitor Management', url: 'https://hubs.ly/Q04tm1Wm0', icon: visitorManagementIcon }
		]
	},
	{
		name: 'Student Behavior and Well-being',
		products: [
			{ name: 'Behavioral Case Manager', url: 'https://hubs.la/Q04tm1V60', icon: behavioralCaseManagerIcon },
			{ name: 'Compass Curriculum', url: 'https://hubs.la/Q04tm2kf0', icon: compassCurriculumIcon },
			{ name: 'PBIS Rewards', url: 'https://hubs.la/Q04tm2vD0', icon: pbisRewardsIcon }
		]
	}
];

const EXPLORE_ALL_URL = 'https://hubs.la/Q04tpJ3F0';

const styles = makeStyles(theme => ({
	paper: {
		'& *': {
			fontFamily: NUNITO_SANS
		},
		fontFamily: NUNITO_SANS,
		width: 460,
		minHeight: 407,
		[theme.breakpoints.down('xs')]: {
			width: '90vw'
		},
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		padding: theme.spacing(2),
		borderRadius: 20,
		border: `1px solid ${BORDER_COLOR}`,
		backgroundColor: theme.palette.common.white,
		boxShadow: theme.shadows[4],
		overflow: 'hidden'
	},
	header: {
		padding: theme.spacing(0, 0.5)
	},
	title: {
		fontSize: "18px",
		fontWeight: 700,
		color: '#29353D'
	},
	subtitle: {
		fontSize: '14px',
		color: '#29353D',
		fontWeight: 400,
		marginTop: theme.spacing(0.5)
	},
	divider: {
		backgroundColor: BORDER_COLOR
	},
	headerDivider: {
		backgroundColor: '#D9DCDD',
		// marginLeft: theme.spacing(-2),
		// marginRight: theme.spacing(-2)
	},
	category: {
		padding: 0
	},
	categoryTitle: {
		color: '#697277',
		fontSize: '12px',
		fontWeight: 700,
		marginBottom: theme.spacing(1.5)
	},
	productGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: theme.spacing(1),
		[theme.breakpoints.down('xs')]: {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
		}
	},
	productTile: {
		alignContent: 'center',
		borderRadius: theme.spacing(1),
		color: '#29353D',
		display: 'grid',
		fontSize: '14px',
		fontWeight: 700,
		gridTemplateRows: '34px 43px',
		justifyItems: 'center',
		lineHeight: 1.35,
		minHeight: 86,
		paddingTop: '12px',
		paddingRight: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		paddingLeft: theme.spacing(0.5),
		rowGap: theme.spacing(1),
		textAlign: 'center',
		textDecoration: 'none',
		'&:focus-visible': {
			outline: '3px solid #99CBD2',
			outlineOffset: 2
		},
		'&:hover': {
			backgroundColor: '#F4F5F5'
		}
	},
	disabledTile: {
		'&:hover': {
			backgroundColor: 'transparent'
		}
	},
	disabledIcon: {
		cursor: 'not-allowed'
	},
	productIcon: {
		display: 'block',
		height: 34,
		width: 40
	},
	productName: {
		alignSelf: 'start',
		color: '#29353D',
		fontSize: '14px',
		fontWeight: 700,
	},
	tooltip: {
		'&:after': {
			display: 'none !important'
		},
		backgroundColor: '#101B2D',
		borderRadius: 8,
		boxShadow: '#E4E7E7',
		color: "#FFFFFF",
		fontFamily: NUNITO_SANS,
		fontSize: "12px",
		fontWeight: 700,
		lineHeight: 1.3,
		maxWidth: "116px",
		padding: "10px"
	},
	tooltipArrow: {
		color: '#101B2D',
		fontSize: '1rem'
	},
	footer: {
		marginTop: theme.spacing(-2),
		paddingTop: theme.spacing(1.5),
		textAlign: 'center'
	},
	exploreLink: {
		color: '#007E8F',
		display: 'inline-block',
		fontSize: '14px',
		fontWeight: 700,
		marginTop: theme.spacing(1.5),
		textDecoration: 'none',
		'&:hover, &:focus': {
			textDecoration: 'underline'
		}
	}
}));

function ProductTile({ product, classes }) {
	const productIcon = (
		<img
			alt=""
			className={`${classes.productIcon} ${!product.url ? classes.disabledIcon : ''}`}
			src={product.icon} />
	);
	const icon = !product.url ? (
		<Tooltip
			arrow
			classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
			placement="left"
			title={CURRENT_APP_TOOLTIP}>
			{productIcon}
		</Tooltip>
	) : productIcon;
	const content = (
		<React.Fragment>
			{icon}
			<span className={classes.productName}>{product.name}</span>
		</React.Fragment>
	);

	if (!product.url) {
		return (
			<span className={`${classes.productTile} ${classes.disabledTile}`} aria-disabled="true">
				{content}
			</span>
		);
	}

	return (
		<a className={classes.productTile} href={product.url} target="_blank" rel="noopener noreferrer">
			{content}
		</a>
	);
}

ProductTile.propTypes = {
	classes: PropTypes.object.isRequired,
	product: PropTypes.shape({
		name: PropTypes.string.isRequired,
		url: PropTypes.string,
		icon: PropTypes.string.isRequired
	}).isRequired
};

function AppSwitcher(props) {
	const classes = styles();
	const { anchorEl, onClose } = props;

	return (
		<Popover
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			onClose={onClose}
			BackdropProps={{ invisible: true, style: { cursor: 'pointer' } }}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			PaperProps={{ className: classes.paper }}
			aria-label="Navigate360 Products">
			<div className={classes.header}>
				<Typography component="h2" className={classes.title}>Navigate360 Products</Typography>
				<Typography className={classes.subtitle}>Learn about a specific Navigate360 product.</Typography>
			</div>
			<Divider className={classes.headerDivider} />
			{PRODUCT_CATEGORIES.map(category => (
				<section className={classes.category} key={category.name} aria-label={category.name}>
					<Typography component="h3" className={classes.categoryTitle}>{category.name}</Typography>
					<div className={classes.productGrid}>
						{category.products.map(product => <ProductTile key={`${category.name}-${product.name}`} product={product} classes={classes} />)}
					</div>
				</section>
			))}
			<div className={classes.footer}>
				<Divider className={classes.divider} />
				<a className={classes.exploreLink} href={EXPLORE_ALL_URL} target="_blank" rel="noopener noreferrer">
					Explore All Navigate360 Solutions <FontAwesomeIcon icon={faExternalLinkAlt} aria-hidden="true" />
				</a>
			</div>
		</Popover>
	);
}

AppSwitcher.propTypes = {
	anchorEl: PropTypes.object,
	onClose: PropTypes.func.isRequired
};

export default AppSwitcher;
